import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { FormControl, Select, MenuItem, OutlinedInput } from "@mui/material";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// hooks
import { useActions } from "../../../common/hooks/useActions";
import { useDidMountEffect } from "../../../common/hooks/useDidMountEffect";
import { useDebounce } from "../../../common/hooks/useDebounce";
// icons
import { dateIcon, timeIcon } from "../../../common/vector";
// helpers
import { hoursUpdate } from "../../../components/AppTimeComponent/helpers/updateHours";
import { updateMinutes } from "../../../components/AppTimeComponent/helpers/updateMinutes";
import { buildEuropcarAvailabilityDateParams } from "./helpers/buildEuropcarAvailabilityDateParams";
import { getFormattedTimeForGetCategoriesCall } from "./helpers/getFormattedTimeForGetCategoriesCall";
import { isReturnAfterPickup } from "./helpers/isReturnAfterPickup";
import { formatDateTimeSelection } from "./helpers/formatDateTimeSelection";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
import { useCookies } from "react-cookie";
// Components
import EuropcarRowAction from "./EuropcarRowAction";
import EuropcarDriverDetailsForm from "./EuropcarDriverDetailsForm";
import EuropcarCarCategoriesList from "./EuropcarCarCategoriesList";
import EuropcarStationOpeningHours from "./EuropcarStationOpeningHours";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
import TransportCancellationPolicy from "../../../components/TransportProviderContent/TransportCancellationPolicy";
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import AppTimeComponent from "../../../components/AppTimeComponent";
import AppButton from "../../../components/ui/AppButton";
import PaymentModal from "../../../components/Payment/PaymentModal";
// actions
import { europcarActionCreators } from "./europcarModule";

type EuropcarStation = {
  station_code: string;
  station_name: string;
  full_address: string;
  distance_km: number;
};

export default function Europcar() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [cookies] = useCookies(["cookieAccepted"]);
  const navigate = useNavigate();
  const carsSectionRef = useRef<HTMLDivElement | null>(null);
  const driverFormRef = useRef<HTMLDivElement | null>(null);

  const language = localStorage.getItem("i18nextLng");

  const now = new Date();
  now.setMinutes(now.getMinutes() + 15); // adding 15 minutes

  // pickup states
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupHours, setPickupHours] = useState(now.getHours());
  const [pickupMinutes, setPickupMinutes] = useState(now.getMinutes());
  // return states (no preselection - user must manually choose)
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  const [returnHours, setReturnHours] = useState(now.getHours());
  const [returnMinutes, setReturnMinutes] = useState(now.getMinutes());
  //
  const [age, setAge] = useState("26");
  const [country, setCountry] = useState<{ code: string; name: string } | null>(null);
  const [selectedStationCode, setSelectedStationCode] = useState<string>("");
  // Validation
  const [showLocationMandatoryMsg, setShowLocationMandatoryMsg] = useState(false);
  const [showReturnDateRequiredMsg, setShowReturnDateRequiredMsg] = useState(false);
  // Form & selected car
  const [selectedCar, setSelectedCar] = useState<any>(null);

  // actions
  const getEuropcarNearestStations = useActions(europcarActionCreators?.getEuropcarNearestStations, []);
  const resetEuropcarState = useActions(europcarActionCreators?.resetEuropcarState, []);
  const getEuropcarCarCategories = useActions(europcarActionCreators?.getEuropcarCarCategories, []);
  const getNationalityCountries = useActions(europcarActionCreators?.getNationalityCountriesAction, []);
  const setCarCategoriesAvailability = useActions(europcarActionCreators?.setCarCategoriesAvailabilityAction, []);
  const getEuropcarInitial = useActions(europcarActionCreators?.getEuropcarInitialAction, []);
  const buyEuropcarTicket = useActions(europcarActionCreators?.buyTicketAction, []);
  const toggleEuropcarPaymentModal = useActions(europcarActionCreators?.toggleEuropcarPaymentModalAction, []);
  const getStationWorkingTime = useActions(europcarActionCreators?.getStationWorkingTimeAction, []);

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const europcarStations = useSelector((state: any) => state.europcar.europcarStations);
  const europcarStationsError = useSelector((state: any) => state.europcar.europcarStationsError);
  const europcarCarCategories = useSelector((state: any) => state.europcar.europcarCarCategories);
  const countries = useSelector((state: any) => state.europcar.countries);
  const isCarCategoriesAvailable = useSelector((state: any) => state.europcar.isCarCategoriesAvailable);
  const europcarCarCategoriesError = useSelector((state: any) => state.europcar.europcarCarCategoriesError);
  const europcarInitial = useSelector((state: any) => state.europcar.europcarInitial);
  const europcarInitialItem = useSelector((state: any) => state.europcar.europcarInitialItem);
  const europcarInitialError = useSelector((state: any) => state.europcar.europcarInitialError);
  const europcarBookingResponseData = useSelector((state: any) => state.europcar.europcarBookingResponseData);
  const showEuropcarPaymentModal = useSelector((state: any) => state.europcar.showEuropcarPaymentModal);
  const europcarBookingError = useSelector((state: any) => state.europcar.europcarBookingError);
  const europcarBookingErrorMessage = useSelector((state: any) => state.europcar.bookingErrorMessage);
  const stationWorkingTime = useSelector((state: any) => state.europcar.stationWorkingTime);
  const stationWorkingTimeError = useSelector((state: any) => state.europcar.stationWorkingTimeError);
  const stationWorkingTimeLoading = useSelector((state: any) => state.europcar.stationWorkingTimeLoading);

  const selectedStation: EuropcarStation | undefined =
    europcarStations?.length > 0 &&
    europcarStations?.find((s: EuropcarStation) => s.station_code === selectedStationCode);

  useEffect(() => {
    if (tenantInfo) {
      getEuropcarInitial();
      getEuropcarNearestStations();
      getNationalityCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantInfo]);

  useEffect(() => {
    if (countries?.CH) {
      setCountry({ code: "CH", name: countries.CH }); // pre-select country
    }
  }, [countries]);

  useEffect(() => {
    // Scroll down to the Cars Section only when cars are returned
    if (isCarCategoriesAvailable && europcarCarCategories?.length > 0) {
      carsSectionRef?.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isCarCategoriesAvailable, europcarCarCategories]);

  // If the language has been changed, we should update the URL
  useDidMountEffect(() => {
    navigate(`/${language}/transport/europcar`, { replace: true });
  }, [language]);

  // reset on unmount
  useEffect(() => {
    return () => {
      resetEuropcarState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callGetStationWorkingTime = () => {
    if (selectedStationCode && returnDate) {
      getStationWorkingTime(selectedStationCode, {
        pickup_date: pickupDate,
        pickup_time: getFormattedTimeForGetCategoriesCall(pickupHours, pickupMinutes),
        return_date: returnDate,
        return_time: getFormattedTimeForGetCategoriesCall(returnHours, returnMinutes)
      });
    }
  };

  const debouncedGetStationWorkingTime = useDebounce(callGetStationWorkingTime, 400);

  useEffect(() => {
    debouncedGetStationWorkingTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStationCode, pickupDate, pickupHours, pickupMinutes, returnDate, returnHours, returnMinutes]);

  const shouldEnableSearchBtn = (): boolean => {
    if (!returnDate) return false;
    const isReturnValid: boolean = isReturnAfterPickup(
      pickupDate,
      pickupHours,
      pickupMinutes,
      returnDate,
      returnHours,
      returnMinutes
    );
    return isReturnValid;
  };

  const cannotProceedDueToStationAvailability = (): boolean => {
    if (stationWorkingTimeError) return true;
    // Once station + return are set, require an affirmative availability check
    // (blocks while loading, while data is missing, and when the station is unavailable).
    if (selectedStationCode && returnDate) {
      return stationWorkingTimeLoading || stationWorkingTime?.is_fully_available !== true;
    }
    return false;
  };

  const renderPrebookErrorMsg = () => {
    let errors: Array<any> = [];

    if (showReturnDateRequiredMsg && !returnDate) {
      errors.push({
        errorMessage: t("transport.returnDateRequired")
      });
    } else if (returnDate && !shouldEnableSearchBtn()) {
      errors.push({
        errorMessage: t("transport.returnTimeValidationErrorMsg")
      });
    }

    if (showLocationMandatoryMsg) {
      errors.push({
        errorMessage: t("transport.pickupReturnRequired")
      });
    }

    if (europcarCarCategoriesError || europcarInitialError) {
      errors.push({
        errorMessage: t("transport.prebookCustomError")
      });
    }

    if (europcarStationsError) {
      errors.push({
        errorMessage: t("transport.stationsLoadError")
      });
    }

    if (stationWorkingTimeError) {
      errors.push({
        errorMessage: t("transport.stationWorkingTimeError")
      });
    }

    if (stationWorkingTime && stationWorkingTime.is_fully_available === false) {
      errors.push({
        errorMessage: stationWorkingTime.message || t("transport.stationNotFullyAvailable")
      });
    }

    return errors?.map((error: any, i: number) => {
      if (!error?.errorMessage) return null;
      return (
        <div key={i} className={`europcar__error-msg ${error?.info ? "europcar__error-msg--info" : ""}`}>
          {error?.field && `${error?.field} -`} {error?.errorMessage}
        </div>
      );
    });
  };

  const renderBookErrorMsg = () => {
    // fallback error message, if there are no error messages from API
    if (!europcarBookingErrorMessage || europcarBookingErrorMessage?.length === 0) {
      return (
        <Box mt={1} className="europcar__error-msg">
          {t("transport.prebookCustomError")}
        </Box>
      );
    }

    return europcarBookingErrorMessage?.map((error: any) => (
      <Box mt={1} className="europcar__error-msg" key={error?.field || Math.random()}>
        {error?.errorMessage ? `${error.errorMessage}` : t("transport.prebookCustomError")}
      </Box>
    ));
  };

  const handleShowCarBtn = () => {
    if (!returnDate) {
      setShowReturnDateRequiredMsg(true);
      return;
    }
    if (!shouldEnableSearchBtn()) {
      return;
    }
    if (!selectedStation?.station_code) {
      setShowLocationMandatoryMsg(true);
      return;
    }
    if (cannotProceedDueToStationAvailability()) {
      return;
    }

    setShowLocationMandatoryMsg(false);
    setShowReturnDateRequiredMsg(false);

    getEuropcarCarCategories({
      offerCode: europcarInitial?.items?.[0]?.code,
      stationId: selectedStationCode,
      ...buildEuropcarAvailabilityDateParams({
        pickupDate,
        returnDate,
        pickupHours,
        pickupMinutes,
        returnHours,
        returnMinutes
      }),
      driver_country: country?.code,
      driver_age: age
    });
  };

  const getDisabledStyleForTicketsForm = () => {
    if (europcarCarCategories?.length > 0 && isCarCategoriesAvailable) {
      return { opacity: 0.4, pointerEvents: "none" };
    }
  };

  const handeEditTicketOptionButton = () => {
    setCarCategoriesAvailability(false);
    setSelectedCar(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleOnSelectCar = (car: any) => {
    setSelectedCar(car);
    driverFormRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleBookAndPay = () => {
    if (!selectedCar || !selectedStation || !returnDate) return;

    const pickupDateTime = new Date(pickupDate);
    pickupDateTime.setHours(pickupHours);
    pickupDateTime.setMinutes(pickupMinutes);

    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: europcarInitialItem?.code ?? selectedCar?.code ?? null
    });

    buyEuropcarTicket(
      {
        start_date: Math.round(+pickupDateTime / 1000), // unix timestamp
        timeslot: `${pickupHours}:${pickupMinutes}`,
        pickup_station_id: selectedStation?.station_code,
        return_station_id: selectedStation?.station_code,
        ...buildEuropcarAvailabilityDateParams({
          pickupDate,
          returnDate,
          pickupHours,
          pickupMinutes,
          returnHours,
          returnMinutes
        }),
        selectedCar,
        countryOfResidence: country?.code
      },
      navigate
    );
  };

  const cancellationPolicy = europcarInitialItem?.cancellation_policy || europcarInitial?.cancellation_policy;

  return (
    <Grid
      className="europcar grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      <>
        <TransportProviderHeader
          title={europcarInitial?.provider_title ?? europcarInitial?.title}
          fallbackTitle="EUROPCAR"
          titleClassName="europcar__title"
          shortDescription={europcarInitial?.provider_description}
          providerLogo={europcarInitial?.provider_logo}
        />

        <Grid container>
          {/* Left side section */}

          <Grid
            item
            sx={{ display: "flex", flexDirection: "column", ...getDisabledStyleForTicketsForm() }}
            md={6}
            xs={12}
          >
            <EuropcarRowAction
              label={t("transport.pickupDateLabel")}
              icon={dateIcon?.default}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppDatePickerCustom
                    selectedDate={pickupDate}
                    setSelectedDate={date => setPickupDate(date ?? new Date())}
                    minDate={new Date()}
                    dataAttribute="europcar-pickup-date-picker"
                  />
                </Grid>
              }
            />
            <EuropcarRowAction
              label={t("transport.pickupTimeLabel")}
              icon={timeIcon?.default}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppTimeComponent
                    hours={pickupHours}
                    minutes={pickupMinutes}
                    handleHoursChange={(change: string, amount: number) => {
                      hoursUpdate(setPickupHours, change, amount, pickupHours);
                    }}
                    handleMinutesChange={(change: string, amount: number) => {
                      updateMinutes(setPickupMinutes, setPickupHours, change, amount, pickupHours, pickupMinutes);
                    }}
                    timeBoxSize="w-80"
                  />
                </Grid>
              }
            />
            <div className="horizontal-divider" />
            <EuropcarRowAction
              label={t("transport.returnDateLabel")}
              icon={dateIcon?.default}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppDatePickerCustom
                    selectedDate={returnDate}
                    setSelectedDate={date => {
                      setReturnDate(date);
                      setShowReturnDateRequiredMsg(false);
                    }}
                    minDate={new Date()}
                    dataAttribute="europcar-return-date-picker"
                  />
                </Grid>
              }
            />
            <EuropcarRowAction
              label={t("transport.returnTimeLabel")}
              icon={timeIcon?.default}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppTimeComponent
                    hours={returnHours}
                    minutes={returnMinutes}
                    handleHoursChange={(change: string, amount: number) => {
                      hoursUpdate(setReturnHours, change, amount, returnHours);
                    }}
                    handleMinutesChange={(change: string, amount: number) => {
                      updateMinutes(setReturnMinutes, setReturnHours, change, amount, returnHours, returnMinutes);
                    }}
                    timeBoxSize="w-80"
                  />
                </Grid>
              }
            />
          </Grid>

          {/* Right side section */}

          <Grid item md={6} xs={12} mt={{ sm: 6, xs: 6, md: 0 }} position={"relative"}>
            <div className="vertical-divider" />
            <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }} sx={{ ...getDisabledStyleForTicketsForm() }}>
              <Box>
                {/* SEARCH INPUT */}
                <FormControl fullWidth>
                  <Box sx={{ textAlign: "left", fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.pickupReturnLocationLabel")}
                  </Box>

                  <Select
                    value={selectedStationCode}
                    displayEmpty
                    onChange={e => {
                      setSelectedStationCode(e.target.value);
                      setShowLocationMandatoryMsg(false);
                    }}
                    input={
                      <OutlinedInput
                        sx={{
                          mt: 1,
                          height: "52px !important"
                        }}
                        data-cy="europcar-location-select-input"
                      />
                    }
                    data-cy="europcar-location-select"
                    renderValue={value => {
                      if (!value || !selectedStation) {
                        return <Box sx={{ color: "text.disabled" }}>{t("transport.chooseLocationPlaceholder")}</Box>;
                      }

                      return (
                        <Box sx={{ lineHeight: 1.4 }}>
                          <Box sx={{ fontSize: 13, fontFamily: "Neutraface2Demi" }}>
                            {selectedStation?.station_name}
                          </Box>
                          <Box sx={{ fontSize: 11, color: "text.secondary" }}>{selectedStation?.full_address}</Box>
                        </Box>
                      );
                    }}
                  >
                    {europcarStations?.length > 0 &&
                      europcarStations.map((station: EuropcarStation) => (
                        <MenuItem key={station?.station_code} value={station?.station_code}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: 2,
                              fontFamily: "Neutraface2Book"
                            }}
                          >
                            {/* LEFT */}
                            <Box>
                              <Box sx={{ fontSize: 13, fontFamily: "Neutraface2Demi" }}>{station?.station_name}</Box>
                              <Box sx={{ fontSize: 11, color: "text.secondary" }}>{station?.full_address}</Box>
                            </Box>

                            {/* RIGHT */}
                            {/* TODO: Remove this once the distance is implemented on the backend */}
                            {/*                             <Box
                              sx={{
                                fontSize: 11,
                                color: "text.secondary",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {station?.distance_km && formatDistance(station?.distance_km)}
                            </Box> */}
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {/* DROPDOWNS */}
                <Box sx={{ display: "flex", gap: 2 }} mt={2}>
                  <Box>
                    <Box pb={1} sx={{ textAlign: "left", fontSize: { xs: 12, sm: 14 } }}>
                      {t("transport.ageLabel")}
                    </Box>
                    <FormControl sx={{ minWidth: 206 }}>
                      <Select
                        value={age}
                        onChange={e => setAge(e.target.value)}
                        input={<OutlinedInput data-cy="europcar-age-select-input" />}
                        data-cy="europcar-age-select"
                      >
                        <MenuItem value="18">18</MenuItem>
                        <MenuItem value="19">19</MenuItem>
                        <MenuItem value="20">20</MenuItem>
                        <MenuItem value="21">21</MenuItem>
                        <MenuItem value="22">22</MenuItem>
                        <MenuItem value="23">23</MenuItem>
                        <MenuItem value="24">24</MenuItem>
                        <MenuItem value="25">25</MenuItem>
                        <MenuItem value="26">26+</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box>
                    <Box pb={1} sx={{ textAlign: "left", fontSize: { xs: 12, sm: 14 } }}>
                      {t("transport.countryLabel")}
                    </Box>
                    <FormControl sx={{ minWidth: 206 }}>
                      <Select
                        value={country ? JSON.stringify(country) : ""}
                        onChange={e => setCountry(JSON.parse(e.target.value))}
                        input={<OutlinedInput data-cy="europcar-country-select-input" />}
                        data-cy="europcar-country-select"
                      >
                        {countries &&
                          Object.entries(countries).map(([code, name]: any) => (
                            <MenuItem key={code} value={JSON.stringify({ code, name })}>
                              {name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{ opacity: shouldEnableSearchBtn() && !cannotProceedDueToStationAvailability() ? 1 : 0.8, mt: 3 }}
                >
                  <AppButton
                    label={t("transport.showCarsButton")}
                    onClick={handleShowCarBtn}
                    disableRipple={true}
                    customBackgroundColor={tenantInfo?.style?.button?.color}
                    customActiveColor={tenantInfo?.style?.button?.active}
                    customHoverColor={tenantInfo?.style?.button?.hover}
                    customClickColor={tenantInfo?.style?.button?.click}
                    fontSize="14px"
                    disabled={cannotProceedDueToStationAvailability()}
                    dataAttribute="europcar-show-cars-button"
                    isLoading={stationWorkingTimeLoading}
                  />
                </Grid>

                <Box mt={1}>{renderPrebookErrorMsg()}</Box>

                {europcarCarCategories?.length === 0 && isCarCategoriesAvailable && (
                  <Box className="europcar__error-msg">{t("transport.noAvailableCarsMessage")}</Box>
                )}

                {stationWorkingTime?.opening_hours?.length > 0 && (
                  <EuropcarStationOpeningHours
                    label={stationWorkingTime.opening_hours_label}
                    rows={stationWorkingTime.opening_hours}
                  />
                )}
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            pt={{ xs: 4, sm: 6 }}
            ml={{ xs: 2, sm: 5, md: 6 }}
            mr={{ xs: 2, sm: 5, md: 6 }}
            ref={carsSectionRef}
          >
            {/* Available Car List */}

            {europcarCarCategories?.length > 0 && isCarCategoriesAvailable && (
              <>
                <Box
                  sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}
                  mb={3}
                >
                  <Box>
                    <Box className="summary-option-title" mb={2}>
                      {t("transport.ticketOption")}
                    </Box>
                    <Box sx={{ flexDirection: "row", display: "flex" }} mb={1}>
                      <Box pr={1}>{t("transport.rentalLocationLabel")}</Box>
                      <Box className="summary-option-title default-text-transform">
                        {selectedStation?.station_name}, {selectedStation?.full_address}
                      </Box>
                    </Box>
                    <Box sx={{ flexDirection: "row", display: "flex" }} mb={1}>
                      <Box pr={1}>{t("transport.pickupLabel")}</Box>
                      <Box className="summary-option-title default-text-transform">
                        {formatDateTimeSelection(pickupDate, pickupHours, pickupMinutes)}
                      </Box>
                    </Box>
                    <Box sx={{ flexDirection: "row", display: "flex" }} mb={1}>
                      <Box pr={1}>{t("transport.returnLabel")}</Box>
                      <Box className="summary-option-title default-text-transform">
                        {returnDate ? formatDateTimeSelection(returnDate, returnHours, returnMinutes) : ""}
                      </Box>
                    </Box>
                  </Box>
                  <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
                    <AppButton
                      label={t("transport.editBtn")}
                      onClick={handeEditTicketOptionButton}
                      disableRipple={true}
                      customBackgroundColor={theme.palette.primary.dark}
                      customHoverColor={theme.palette.primary.dark}
                      fontSize="14px"
                      dataAttribute="europcar-edit-button"
                    />
                  </Grid>
                </Box>
                <EuropcarCarCategoriesList
                  carCategories={europcarCarCategories}
                  onSelect={car => handleOnSelectCar(car)}
                  tenantInfo={tenantInfo}
                  selectedCar={selectedCar}
                />
              </>
            )}
          </Grid>

          {europcarCarCategories?.length > 0 && isCarCategoriesAvailable && (
            <>
              <Grid
                item
                xs={12}
                pt={{ xs: 4, sm: 6 }}
                ml={{ xs: 2, sm: 5, md: 6 }}
                mr={{ xs: 2, sm: 5, md: 6 }}
                ref={driverFormRef}
              >
                {/* Driver and Vehicle Details FORM */}
                <EuropcarDriverDetailsForm selectedCar={selectedCar} />

                {/* Render booking error message */}
                {europcarBookingError && renderBookErrorMsg()}
              </Grid>

              {cancellationPolicy && (
                <Grid item xs={12} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
                  <Box pt={6}>
                    <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
                  </Box>
                </Grid>
              )}

              {/* BOOK BUTTON */}
              <Grid
                item
                sx={{
                  width: { xs: "100%", sm: "100%", md: "40%" },
                  margin: cancellationPolicy?.trim() ? "24px auto 0 auto" : "25px auto 0 auto"
                }}
                pl={2}
                pr={2}
              >
                <AppButton
                  label={t("transport.bookAndPayButton")}
                  onClick={handleBookAndPay}
                  disableRipple={true}
                  customBackgroundColor={tenantInfo?.style?.button?.color}
                  customActiveColor={tenantInfo?.style?.button?.active}
                  customHoverColor={tenantInfo?.style?.button?.hover}
                  customClickColor={tenantInfo?.style?.button?.click}
                  fontSize="14px"
                  disabled={!selectedCar}
                  dataAttribute="europcar-book-and-pay-button"
                />
              </Grid>
            </>
          )}
        </Grid>
      </>
      {showEuropcarPaymentModal && (
        <PaymentModal
          isOpen={showEuropcarPaymentModal}
          bookingResponseData={europcarBookingResponseData}
          togglePaymentModal={toggleEuropcarPaymentModal}
          redirectUrl="transport/europcar/confirmation"
        />
      )}
    </Grid>
  );
}
